import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle2, Clock, MapPin, User, ArrowLeft, Phone, Calendar, Printer, FileText, ShoppingBag, ShieldCheck, XCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import TokenExpiredError from '../components/TokenExpiredError';
import { orderSessionService } from '../services/api/orderSessionService';
import { publicOrderService } from '../services/api/publicOrderService';
import Badge from '../components/common/Badge';
import Card from '../components/common/Card';
import Alert from '../components/common/Alert';
import IconButton from '../components/common/IconButton';
import Stepper from '../components/common/Stepper';
import Button from '../components/common/Button';

const OrderStatusPage = () => {
    const { orderId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const rawToken = searchParams.get('token');
    const token = (rawToken && rawToken !== 'null' && rawToken !== 'undefined') ? rawToken : null;
    const from = searchParams.get('from');
    const activeToken = token || localStorage.getItem('customer_token') || sessionStorage.getItem('customer_token');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [order, setOrder] = useState(null);
    const [supplierData, setSupplierData] = useState(null);
    const [printFormat, setPrintFormat] = useState('a4'); // 'a4' or 'thermal'

    useEffect(() => {
        const loadOrderDetails = async () => {
            if (!activeToken) {
                setError('Invalid link. Please use the link provided in your order confirmation.');
                setLoading(false);
                return;
            }

            try {
                // 1. Validate Token & Get Context
                const session = await orderSessionService.getSessionContext(activeToken);
                if (!session.valid) {
                    setError(session.errorMessage || 'Session expired');
                    setLoading(false);
                    return;
                }

                setSupplierData(session.tenant);

                // 2. Fetch Order Details using context
                const orderData = await publicOrderService.getOrder(
                    orderId,
                    session.customer.publicId,
                    session.tenant.supplierId
                );

                setOrder(orderData);
                setLoading(false);

            } catch (err) {
                console.error('Failed to load order status:', err);
                setError('Failed to load order details. Please try again.');
                setLoading(false);
            }
        };

        if (orderId && activeToken) {
            loadOrderDetails();
        }
    }, [orderId, activeToken]);

    if (loading) return <LoadingSpinner message="Tracking your order..." />;
    if (error) return <TokenExpiredError message={error} />;
    if (!order) return <TokenExpiredError message="Order not found" />;

    const getStatusIndex = (status) => {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower === 'cancelled') return -1;
        if (statusLower === 'new' || statusLower === 'created' || statusLower === 'new_order' || statusLower === 'acknowledged') return 0;
        if (statusLower === 'processing' || statusLower === 'pending_warehouse' || statusLower === 'in_warehouse' || statusLower === 'sent_to_warehouse') return 1;
        if (statusLower === 'confirmed' || statusLower === 'ready' || statusLower === 'despatched' || statusLower === 'dispatched' || statusLower === 'packed') return 2;
        if (statusLower === 'delivered' || statusLower === 'completed') return 3;
        return 0; // Default
    };

    const currentStep = getStatusIndex(order.status);

    const getBadgeVariant = (status) => {
        switch (status?.toLowerCase()) {
            case 'new': 
            case 'created':
            case 'new_order':
            case 'acknowledged':
                return 'info';
            case 'processing': 
            case 'pending_warehouse':
            case 'in_warehouse':
            case 'sent_to_warehouse':
                return 'warning';
            case 'confirmed':
            case 'ready': 
            case 'despatched':
            case 'dispatched':
            case 'packed':
                return 'primary';
            case 'delivered': 
            case 'completed':
                return 'success';
            case 'cancelled': 
                return 'danger';
            default: 
                return 'neutral';
        }
    };

    const formatStatusTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const datePart = date.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
        const timePart = date.toLocaleTimeString(undefined, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
        return `${datePart} • ${timePart}`;
    };

    const steps = [
        { 
            label: 'Order Placed', 
            desc: 'Received by supplier', 
            icon: <FileText size={18} />,
            time: order.createdAt ? formatStatusTime(order.createdAt) : null
        },
        { 
            label: 'Confirmed', 
            desc: 'Processing in warehouse', 
            icon: <Clock size={18} />,
            time: currentStep === 1 && order.updatedAt ? formatStatusTime(order.updatedAt) : null
        },
        { 
            label: 'Ready/Despatched', 
            desc: 'Packaged & ready for transit', 
            icon: <Package size={18} />,
            time: currentStep === 2 && order.updatedAt ? formatStatusTime(order.updatedAt) : null
        },
        { 
            label: 'Delivered', 
            desc: 'Arrived at your location', 
            icon: <Truck size={18} />,
            time: currentStep === 3 && order.updatedAt ? formatStatusTime(order.updatedAt) : null
        }
    ];

    const handleBack = () => {
        if (from === 'history') {
            navigate(`/status/orders?token=${activeToken}`);
        } else if (from === 'pending') {
            navigate(`/shop?token=${activeToken}&tab=Pending`);
        } else {
            if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate(`/shop?token=${activeToken}`);
            }
        }
    };

    return (
        <div className="h-[100dvh] overflow-y-auto bg-gradient-to-br from-slate-50 to-cyan-50/20 dark:from-slate-900 dark:to-slate-950 flex flex-col font-sans">
            {/* Header */}
            <header className="bg-white/80 dark:bg-slate-800/85 backdrop-blur-md border-b border-slate-100 dark:border-slate-700/50 sticky top-0 z-30 px-4 py-3 sm:px-6 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 print:hidden">
                <div className="flex items-center gap-3">
                    <IconButton
                        onClick={handleBack}
                        icon={ArrowLeft}
                        ariaLabel="Go back"
                        variant="secondary"
                        size="md"
                    />
                    <div>
                        <h1 className="text-base sm:text-lg font-black text-slate-900 dark:text-slate-50 tracking-tight leading-none">Track Order</h1>
                        <p className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider mt-0.5">#{order.orderRefNo || order.publicId.substring(0, 8)}</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                    <select
                        value={printFormat}
                        onChange={(e) => setPrintFormat(e.target.value)}
                        className="flex-1 sm:flex-initial px-3 py-2 rounded-xl text-xs font-bold bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 outline-none focus:ring-2 focus:ring-cyan-500 cursor-pointer text-slate-700 dark:text-slate-200"
                    >
                        <option value="a4">Standard A4 Invoice</option>
                        <option value="thermal">4" Thermal Receipt</option>
                    </select>
                    <Button
                        onClick={() => window.print()}
                        variant="secondary"
                        size="sm"
                        className="px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-1.5 whitespace-nowrap active:scale-95 transition-all flex-shrink-0"
                    >
                        <Printer className="w-3.5 h-3.5" /> Save / Print
                    </Button>
                </div>
            </header>

            <div className="flex-1 max-w-lg lg:max-w-6xl w-full mx-auto px-4 py-6 flex flex-col lg:grid lg:grid-cols-12 gap-6 items-start print:hidden">
                
                {/* Left Column - Stepper & Details */}
                <div className="w-full flex flex-col gap-6 lg:col-span-7">
                    {/* --- RENDER CANCELLED NOTICE --- */}
                    {currentStep === -1 && (
                        <Alert variant="danger" title="Order Cancelled" icon={<XCircle className="w-6 h-6 text-rose-500" />}>
                            This order has been cancelled. If this is an error or you need assistance, please contact your supplier.
                        </Alert>
                    )}

                    {/* --- TRACKING TIMELINE CARD --- */}
                    {currentStep !== -1 && (
                        <Card className="flex flex-col gap-6">
                            <div className="flex justify-between items-center pb-4 border-b border-slate-50 dark:border-slate-700/50">
                                <div>
                                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">Order Status</p>
                                    <h2 className="text-xl font-black text-slate-900 dark:text-slate-50 capitalize flex items-center gap-2">
                                        {order.status?.toLowerCase().replace('_', ' ')}
                                    </h2>
                                </div>
                                <Badge variant={getBadgeVariant(order.status)}>
                                    {order.status}
                                </Badge>
                            </div>

                            {/* Interactive Timeline Stepper */}
                            <Stepper steps={steps} currentStep={currentStep} />
                        </Card>
                    )}

                    {/* --- DETAILS TILES --- */}
                    <Card className="flex flex-col gap-6">
                        <h3 className="font-black text-slate-900 dark:text-slate-50 text-base">Order Information</h3>
                        
                        <div className="grid grid-cols-1 gap-5">
                            {/* Delivery Schedule */}
                            {order.deliveryDate && (
                                <div className="flex gap-3.5 items-start">
                                    <div className="bg-cyan-50 dark:bg-cyan-950/20 text-cyan-600 dark:text-cyan-400 w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border border-cyan-100/50 dark:border-cyan-900/30">
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Estimated Delivery</p>
                                        <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                                            {new Date(order.deliveryDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                        {order.timeSlot && <span className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 block">{order.timeSlot}</span>}
                                    </div>
                                </div>
                            )}

                            {/* Customer Bill To */}
                            <div className="flex gap-3.5 items-start">
                                <div className="bg-cyan-50 dark:bg-cyan-950/20 text-cyan-600 dark:text-cyan-400 w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border border-cyan-100/50 dark:border-cyan-900/30">
                                    <User className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Bill To</p>
                                    <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">{order.customer?.name || 'Customer'}</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 leading-relaxed">{order.customer?.address || order.customer?.city || 'No address details'}</p>
                                </div>
                            </div>

                            {/* Salesperson / Representative */}
                            {order.assignedSalesPersonName && (
                                <div className="flex gap-3.5 items-start">
                                    <div className="bg-cyan-50 dark:bg-cyan-950/20 text-cyan-600 dark:text-cyan-400 w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border border-cyan-100/50 dark:border-cyan-900/30">
                                        <User className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Assigned Agent</p>
                                        <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">{order.assignedSalesPersonName}</p>
                                        {order.assignedSalesPersonMobile && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">📱 {order.assignedSalesPersonMobile}</p>}
                                    </div>
                                </div>
                            )}

                            {/* Special Instructions / Notes */}
                            {order.notes && (
                                <div className="flex gap-3.5 items-start p-3.5 bg-amber-50 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-900/40 rounded-2xl">
                                    <div className="bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border border-amber-200 dark:border-amber-800">
                                        <FileText className="w-5 h-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-[10px] font-black text-amber-800 dark:text-amber-400 uppercase tracking-wider mb-0.5">Special Instructions</p>
                                        <p className="text-xs font-semibold text-amber-900 dark:text-amber-200 leading-relaxed">{order.notes}</p>
                                    </div>
                                </div>
                            )}

                            {/* Contact Supplier */}
                            <div className="flex gap-3.5 items-start pt-4 border-t border-slate-50 dark:border-slate-700/50">
                                <div className="bg-cyan-50 dark:bg-cyan-950/20 text-cyan-600 dark:text-cyan-400 w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 border border-cyan-100/50 dark:border-cyan-900/30">
                                    <Phone className="w-5 h-5" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Supplier Help</p>
                                    <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">{supplierData?.businessName}</p>
                                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">📞 {supplierData?.phone || 'Contact supplier for details'}</p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Right Column - Items List */}
                <div className="w-full lg:col-span-5 lg:sticky lg:top-24">
                    {/* --- ITEMS LIST CARD --- */}
                    <Card className="p-0 overflow-hidden print:hidden">
                        <div className="px-6 py-5 border-b border-slate-50 dark:border-slate-700/50 flex items-center justify-between">
                            <h3 className="font-black text-slate-900 dark:text-slate-50 text-base flex items-center gap-2">
                                <ShoppingBag className="w-5 h-5 text-cyan-600" /> Order Items
                            </h3>
                            <span className="text-xs text-slate-400 dark:text-slate-500 font-bold uppercase">{order.items?.length} products</span>
                        </div>
                        <div className="divide-y divide-slate-50 dark:divide-slate-700/50 max-h-96 overflow-y-auto custom-scrollbar">
                            {order.items?.map((item, idx) => (
                                <div key={idx} className="p-5 flex items-center justify-between gap-4">
                                    <div className="flex items-center gap-3.5 min-w-0">
                                        <div className="w-10 h-10 bg-slate-50 dark:bg-slate-700 border border-slate-100 dark:border-slate-600 rounded-xl flex items-center justify-center text-sm font-bold text-slate-500 dark:text-slate-400 flex-shrink-0">
                                            📦
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-bold text-slate-800 dark:text-slate-100 text-sm break-words whitespace-normal">{item.productName}</p>
                                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5 font-semibold flex items-center gap-1.5 flex-wrap">
                                                <span>{item.quantity}</span>
                                                <span className="inline-block px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700/60 text-slate-500 dark:text-slate-400 rounded text-[9px] font-bold">
                                                    {item.unit || 'pcs'}
                                                </span>
                                                <span>× AED {item.unitPrice?.toFixed(2)}</span>
                                            </p>
                                        </div>
                                    </div>
                                    <p className="font-black text-slate-800 dark:text-slate-100 text-sm whitespace-nowrap">AED {(item.subtotal || item.totalPrice || (item.quantity * item.unitPrice))?.toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-800/40 px-6 py-5 border-t border-slate-100 dark:border-slate-700/50 flex justify-between items-center">
                            <div>
                                <p className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-0.5">Total Amount</p>
                                <span className="text-xs text-slate-400 dark:text-slate-500 font-medium">All inclusive</span>
                            </div>
                            <span className="text-2xl font-black text-slate-900 dark:text-slate-50">AED {order.totalAmount?.toFixed(2)}</span>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Print Invoice Layout (Visible only on print) */}
            {printFormat === 'thermal' ? (
                /* Thermal Print Layout */
                <div className="hidden print:block print-invoice-wrapper font-sans text-gray-900">
                    {/* Header */}
                    <div className="text-center pb-3 border-b border-dashed border-gray-400 mb-3 space-y-1">
                        <div className="flex items-center justify-center gap-2">
                            {supplierData?.logoUrl ? (
                                <img src={supplierData.logoUrl} alt="Logo" className="w-8 h-8 object-contain rounded-lg border border-gray-100" />
                            ) : (
                                <div className="w-8 h-8 bg-blue-600 text-white font-black text-sm rounded-lg flex items-center justify-center">
                                    {(supplierData?.businessName || 'S').charAt(0)}
                                </div>
                            )}
                            <h2 className="text-base font-black tracking-tight text-gray-900 uppercase">
                                {supplierData?.businessName || 'Supplier'}
                            </h2>
                        </div>
                        <h1 className="text-sm font-black tracking-tight text-gray-800 uppercase">INVOICE</h1>
                        <p className="text-xs font-black text-gray-800">#{order.orderRefNo || order.publicId.substring(0, 8)}</p>
                        <div className="mt-1">
                            <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-800 text-[9px] font-bold border uppercase">
                                {order.status}
                            </span>
                        </div>
                    </div>

                    {/* Info Block */}
                    <div className="space-y-3 text-xs mb-3">
                        {/* Supplier */}
                        <div className="pb-2 border-b border-dashed border-gray-200">
                            <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Supplier</h3>
                            <p className="font-bold text-gray-900">{supplierData?.businessName || 'Supplier'}</p>
                            <p className="text-gray-600 text-[11px] leading-tight mt-0.5">{supplierData?.address || 'Address N/A'}</p>
                            <p className="text-gray-600 text-[11px]">📞 {supplierData?.phone || 'N/A'}</p>
                        </div>

                        {/* Customer */}
                        <div className="pb-2 border-b border-dashed border-gray-200">
                            <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">Bill To</h3>
                            <p className="font-bold text-gray-900">{order.customer?.name || 'Guest'}</p>
                            <p className="text-gray-600 text-[11px] leading-tight mt-0.5">{order.customer?.address || order.customer?.city || 'No Address'}</p>
                            <p className="text-gray-600 text-[11px]">📱 {order.customer?.mobileNumber || order.customer?.numberOf || 'N/A'}</p>
                        </div>

                        {/* Metadata */}
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pb-2 border-b border-dashed border-gray-200 text-[11px]">
                            <div>
                                <span className="block text-[9px] font-bold text-gray-400 uppercase">Order Date</span>
                                <span className="font-semibold text-gray-900">
                                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}
                                </span>
                            </div>
                            <div>
                                <span className="block text-[9px] font-bold text-gray-400 uppercase">Delivery Date</span>
                                <span className="font-semibold text-gray-900">
                                    {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('en-GB') : 'Pending'}
                                </span>
                            </div>
                            <div>
                                <span className="block text-[9px] font-bold text-gray-400 uppercase">Sales Person</span>
                                <span className="font-semibold text-gray-900 truncate block max-w-[120px]">{order.assignedSalesPersonName || 'Unassigned'}</span>
                            </div>
                            <div>
                                <span className="block text-[9px] font-bold text-gray-400 uppercase">Time Slot</span>
                                <span className="font-semibold text-gray-900">{order.timeSlot || 'Any time'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Items Section */}
                    <div className="mb-3">
                        <h3 className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Items</h3>
                        <div className="space-y-2.5">
                            {order.items?.map((item, idx) => (
                                <div key={idx} className="text-xs pb-1.5 border-b border-dashed border-gray-100 last:border-b-0">
                                    <div className="font-bold text-gray-900 leading-tight">{item.productName || item.name}</div>
                                    <div className="flex justify-between text-[11px] text-gray-600 mt-0.5">
                                        <span>{item.quantity} {item.unit || 'pcs'} x AED {(item.unitPrice || item.price || 0).toFixed(2)}</span>
                                        <span className="font-bold text-gray-900">AED {(item.subtotal || item.totalPrice || (item.quantity * (item.unitPrice || item.price || 0))).toFixed(2)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer Totals */}
                    <div className="border-t border-double border-gray-900 pt-2 flex justify-between items-center text-xs">
                        <span className="font-bold uppercase text-gray-700">Total Amount</span>
                        <span className="text-base font-black text-gray-950">AED {(order.totalAmount || 0).toFixed(2)}</span>
                    </div>

                    {/* Thank you */}
                    <div className="text-center pt-5 text-[9px] text-gray-500 font-semibold uppercase tracking-wider border-t border-dashed border-gray-300 mt-4 font-sans">
                        Thank You for Your Order!
                    </div>
                </div>
            ) : (
                /* Standard A4 Layout */
                <div className="hidden print:block print-invoice-wrapper font-sans text-gray-900">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-8 border-b border-gray-100 pb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">INVOICE</h1>
                            <p className="text-gray-500 font-medium">#{order.orderRefNo || order.publicId}</p>
                            <div className="mt-4 flex gap-2">
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-800 uppercase tracking-wide border border-gray-200">
                                    {order.status}
                                </span>
                            </div>
                        </div>
                        <div className="text-right flex flex-col items-end">
                            <div className="flex items-center gap-2">
                                {supplierData?.logoUrl ? (
                                    <img src={supplierData.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-xl border border-gray-100" />
                                ) : (
                                    <div className="w-10 h-10 bg-blue-600 text-white font-black text-base rounded-xl flex items-center justify-center">
                                        {(supplierData?.businessName || 'S').charAt(0)}
                                    </div>
                                )}
                                <h2 className="text-xl font-black text-gray-900 tracking-tight">{supplierData?.businessName || 'Supplier'}</h2>
                            </div>
                            <p className="text-gray-400 text-xs mt-1">{new Date().toLocaleDateString()}</p>
                        </div>
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-12 mb-8">
                        {/* Customer */}
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Bill To</h3>
                            <div className="text-gray-950 space-y-1.5 text-sm">
                                <p className="font-bold text-lg">{order.customer?.name || 'Guest'}</p>
                                <p className="text-gray-600">{order.customer?.address || order.customer?.city || 'No Address'}</p>
                                <p className="text-gray-600 flex items-center gap-2">
                                    <span className="text-gray-400">📱</span> {order.customer?.mobileNumber || order.customer?.numberOf || 'N/A'}
                                </p>
                            </div>
                        </div>

                        {/* Supplier */}
                        <div>
                            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">Supplier</h3>
                            <div className="text-gray-950 space-y-1.5 text-sm">
                                <p className="font-bold text-lg">{supplierData?.businessName || 'Supplier'}</p>
                                <p className="text-gray-600">{supplierData?.address || 'Address N/A'}</p>
                                <p className="text-gray-600 flex items-center gap-2">
                                    <span className="text-gray-400">📞</span> {supplierData?.phone || 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Meta Info */}
                    <div className="grid grid-cols-4 gap-4 mb-8 bg-gray-50 rounded-xl p-5 border border-gray-100">
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Order Date</p>
                            <p className="font-semibold text-gray-950">
                                {order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Delivery Date</p>
                            <p className="font-semibold text-gray-950">
                                {order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('en-GB') : 'Pending'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Sales Person</p>
                            <div className="space-y-0.5">
                                <p className="font-semibold text-gray-950">{order.assignedSalesPersonName || 'Unassigned'}</p>
                                {order.assignedSalesPersonMobile && (
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                        📱 {order.assignedSalesPersonMobile}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Time Slot</p>
                            <p className="font-semibold text-gray-950">{order.timeSlot || 'Any time'}</p>
                        </div>
                    </div>

                    {/* Items Table */}
                    <div className="mb-8">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b-2 border-gray-100">
                                    <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Item Description</th>
                                    <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Qty</th>
                                    <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Unit Price</th>
                                    <th className="py-3 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Total</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 text-sm">
                                {order.items.map((item, idx) => (
                                    <tr key={idx}>
                                        <td className="py-4 font-medium text-gray-950">{item.productName}</td>
                                        <td className="py-4 text-center text-gray-600">
                                            <span>{item.quantity}</span>
                                            {item.unit && (
                                                <span className="inline-block ml-1.5 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-bold uppercase">
                                                    {item.unit}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-4 text-right text-gray-600">AED {(item.unitPrice || 0).toFixed(2)}</td>
                                        <td className="py-4 text-right font-bold text-gray-950">AED {(item.subtotal || item.totalPrice || (item.quantity * item.unitPrice))?.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Totals */}
                    <div className="flex justify-end border-t border-gray-100 pt-6">
                        <div className="w-64 space-y-3">
                            <div className="flex justify-between items-center text-gray-600">
                                <span>Subtotal</span>
                                <span>AED {(order.totalAmount || 0).toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-cyan-600 font-bold text-xl pt-3 border-t border-gray-100">
                                <span>Total</span>
                                <span>AED {(order.totalAmount || 0).toFixed(2)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @media print {
                    @page {
                        size: ${printFormat === 'thermal' ? '100mm auto' : 'A4 portrait'};
                        margin: ${printFormat === 'thermal' ? '0' : '1.6cm'};
                    }
                    body {
                        background-color: #ffffff;
                        margin: 0;
                        padding: 0;
                    }
                    .print-invoice-wrapper {
                        display: block !important;
                        width: ${printFormat === 'thermal' ? '100mm !important' : '100% !important'};
                        max-width: ${printFormat === 'thermal' ? '100mm !important' : '210mm !important'};
                        padding: ${printFormat === 'thermal' ? '4mm !important' : '0 !important'};
                        box-sizing: border-box !important;
                    }
                }
            `}</style>

            {/* Footer */}
            <footer className="bg-slate-50 dark:bg-slate-800/40 py-6 text-center border-t border-slate-100/60 mt-auto print:hidden">
                <p className="text-slate-400 dark:text-slate-500 text-[9px] font-bold uppercase tracking-wider">
                    Securely powered by <span className="text-slate-500 font-extrabold">Yaseero</span>
                </p>
            </footer>
        </div>
    );
};

export default OrderStatusPage;
